// WB-M1A (doc 23 §13.4.6) -- the board scene codec: the one place the
// browser turns the editor's scene into the envelope Subway persists, and
// the persisted envelope back into what the editor mounts.
//
// The envelope mirrors Subway's whitelist (server/services/
// AiTutorBoardScene.ts), which is the authority: anything this codec lets
// through that Subway does not accept is a visible save failure, never a
// silent one. Rules kept here:
//   - only non-deleted elements of the M1A element types are serialized;
//   - each element carries only the fields Subway knows for its type;
//   - `files` is always empty (no media in M1A);
//   - `link` and `frameId` are always null (no URLs, no frames in M1A);
//   - `customData` keeps only Borna's reserved `borna` namespace;
//   - the view is a whitelist copy: scroll and zoom, nothing else of the
//     editor's state (no selection, tool or editing state).
//
// WB-M1B S2 (doc 23 §16.3) adds the board equation, without a new element
// type on the wire:
//   - AT REST an equation is an ordinary M1A text element -- the CARRIER --
//     whose `text` (= `originalText`) is its LaTeX source and whose
//     `customData.borna` is `{kind: 'equation', v: 1, em}` (plus any keys a
//     later version adds, which travel untouched). Subway, the envelope and
//     `files: {}` are unchanged; frozen WB-M1A shows it as editable text.
//   - AT RUNTIME (inside the editor only) the same element id is an image
//     element whose picture the S1 renderer draws from the source. The file
//     is a local, non-authoritative cache: never sent, re-made on load.
//   - `rehydrateBoardFiles` turns valid carriers into images before the
//     editor mounts; `encodeBoardScene` turns them back into carriers.
//   - A carrier that is not valid (any reason) stays exactly the text element
//     it is: degraded, never dropped.
//
// WB-M1B S3 (doc 23 §19): the editor inserts, edits and resizes runtime
// images. The codec's part is the scale: an image that was resized is
// persisted at the `em` its height means (`runtimeEquationEm`), and
// `equationPictureFor` is the one way a runtime file is made.

import {
  loadEquationEngine,
  renderEquationNow,
  validateEquationSource,
} from './boardEquation'
import {
  equationEmForHeight,
  isValidEquationEm,
  resizeAtAnchor,
} from './boardEquationGeometry'
import { hasUnusablePressure, strokeWithoutPressure } from './boardStroke'

export const BOARD_SCENE_SCHEMA_VERSION = 1
export const BOARD_SCENE_FORMAT = 'excalidraw/0.17'

export const BOARD_ELEMENT_TYPES = [
  'rectangle',
  'diamond',
  'ellipse',
  'line',
  'arrow',
  'freedraw',
  'text',
]

const BOARD_MIN_ZOOM = 0.1
const BOARD_MAX_ZOOM = 30

const BASE_KEYS = [
  'id',
  'type',
  'x',
  'y',
  'width',
  'height',
  'angle',
  'strokeColor',
  'backgroundColor',
  'fillStyle',
  'strokeWidth',
  'strokeStyle',
  'roundness',
  'roughness',
  'opacity',
  'seed',
  'version',
  'versionNonce',
  'isDeleted',
  'groupIds',
  'frameId',
  'boundElements',
  'updated',
  'link',
  'locked',
]

const LINEAR_KEYS = [
  'points',
  'lastCommittedPoint',
  'startBinding',
  'endBinding',
  'startArrowhead',
  'endArrowhead',
]

const TYPE_KEYS = {
  rectangle: [],
  diamond: [],
  ellipse: [],
  line: LINEAR_KEYS,
  arrow: LINEAR_KEYS,
  freedraw: ['points', 'pressures', 'simulatePressure', 'lastCommittedPoint'],
  text: [
    'text',
    'originalText',
    'fontSize',
    'fontFamily',
    'textAlign',
    'verticalAlign',
    'containerId',
    'baseline',
    'lineHeight',
  ],
}

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function finite(value, fallback) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

// ---------------------------------------------------------------------------
// The equation carrier and its runtime image (WB-M1B).
// ---------------------------------------------------------------------------

export const BOARD_EQUATION_KIND = 'equation'
export const BOARD_EQUATION_VERSION = 1
// Keys of `customData.borna` that exist only at runtime and are never sent.
export const BOARD_EQUATION_RUNTIME_KEYS = Object.freeze(['latex'])

// What frozen WB-M1A needs to draw a carrier as readable source text: the
// board's own text defaults (BoardEditor: Helvetica alias = Excalidraw font
// family 2, Excalidraw's default size 20 and its Helvetica line height).
// Frozen WB-M1A re-measures the text itself; these only have to be valid.
const CARRIER_TEXT = Object.freeze({
  fontSize: 20,
  fontFamily: 2,
  textAlign: 'left',
  verticalAlign: 'top',
  containerId: null,
  lineHeight: 1.15,
  baseline: 18,
})

// The file ids this client made from an equation's source: the picture's
// natural size and every source it drew that picture from (two sources can
// draw the same picture -- `a+b` and `a + b` -- and so share one
// content-addressed file). An image element is an equation only if its file
// is one of these, made from its own `latex`: an image from anywhere else
// (a paste, a drop, a crafted scene) is not supported and the editor removes
// it as in M1A.
const runtimeEquationFiles = new Map()

function bornaOf(element) {
  return isPlainObject(element) &&
    isPlainObject(element.customData) &&
    isPlainObject(element.customData.borna)
    ? element.customData.borna
    : null
}

/**
 * How the codec reads a persisted element (pure, synchronous, no engine):
 *   'none'    -- not an equation carrier at all (any ordinary element);
 *   'invalid' -- marked as an equation but not renderable here (unknown
 *                `v`, bad `em`, bad or refused source, not a free text
 *                element): it stays the text element it is;
 *   'valid'   -- a carrier the renderer may be asked to draw.
 * @param {object} element
 * @returns {'none'|'invalid'|'valid'}
 */
export function equationCarrierStatus(element) {
  const borna = bornaOf(element)
  if (!borna || borna.kind !== BOARD_EQUATION_KIND) return 'none'
  if (element.type !== 'text' || element.isDeleted) return 'invalid'
  if (borna.v !== BOARD_EQUATION_VERSION) return 'invalid'
  if (!isValidEquationEm(borna.em)) return 'invalid'
  if (element.containerId !== null && element.containerId !== undefined) {
    return 'invalid'
  }
  if (
    typeof element.text !== 'string' ||
    element.text !== element.originalText
  ) {
    return 'invalid'
  }
  try {
    validateEquationSource(element.text)
  } catch (err) {
    return 'invalid'
  }
  return 'valid'
}

/**
 * Whether an element is a runtime equation image THIS client made: an image
 * whose file this client rendered from exactly the `latex` it carries.
 * @param {object} element
 */
export function isRuntimeEquationImage(element) {
  const borna = bornaOf(element)
  return (
    !!borna &&
    element.type === 'image' &&
    !element.isDeleted &&
    borna.kind === BOARD_EQUATION_KIND &&
    borna.v === BOARD_EQUATION_VERSION &&
    isValidEquationEm(borna.em) &&
    typeof borna.latex === 'string' &&
    typeof element.fileId === 'string' &&
    runtimeEquationFiles.has(element.fileId) &&
    runtimeEquationFiles.get(element.fileId).sources.has(borna.latex)
  )
}

/**
 * The scale of a runtime equation image as it is right now (S3): its stored
 * `em` while its height is still the one that `em` gives, the `em` its
 * height means after a resize (inside the range). An image whose file this
 * client did not make has no natural size here: its stored `em`.
 * @param {object} image
 * @returns {number}
 */
export function runtimeEquationEm(image) {
  const borna = image.customData.borna
  const made = runtimeEquationFiles.get(image.fileId)
  if (!made) return borna.em
  return equationEmForHeight(borna.em, image.height, made.metrics.heightEm)
}

/**
 * The natural size (in em) of the picture behind a file id this client made.
 * @param {string} fileId
 * @returns {{widthEm: number, heightEm: number}|null}
 */
export function runtimeEquationMetrics(fileId) {
  const made = runtimeEquationFiles.get(fileId)
  return made ? made.metrics : null
}

function baseFields(element) {
  const out = {}
  BASE_KEYS.forEach(key => {
    if (element[key] !== undefined) out[key] = element[key]
  })
  return out
}

/**
 * A valid carrier -> its runtime image: same id and every base field
 * (version and versionNonce included, so the scene version is unchanged),
 * the size of the picture at `em`, the same rotated top-left anchor and
 * angle. Pure.
 * @param {object} carrier
 * @param {{widthEm: number, heightEm: number}} metrics -- the picture's
 *   natural size
 * @param {string} fileId
 */
export function equationCarrierToImage(carrier, metrics, fileId) {
  const borna = carrier.customData.borna
  const width = metrics.widthEm * borna.em
  const height = metrics.heightEm * borna.em
  const { x, y } = resizeAtAnchor(carrier, width, height)
  return Object.assign(baseFields(carrier), {
    type: 'image',
    x,
    y,
    width,
    height,
    fileId,
    status: 'saved',
    scale: [1, 1],
    customData: {
      borna: Object.assign({}, borna, { latex: carrier.text }),
    },
  })
}

/**
 * A runtime equation image -> the carrier that is persisted: same id and
 * base fields, the image's own box (so the same rotated top-left anchor),
 * the source as `text` / `originalText`, `borna` without its runtime-only
 * keys and with the `em` the image has now (`runtimeEquationEm`).
 * @param {object} image
 */
export function equationImageToCarrier(image) {
  const borna = Object.assign({}, image.customData.borna, {
    em: runtimeEquationEm(image),
  })
  const source = borna.latex
  BOARD_EQUATION_RUNTIME_KEYS.forEach(key => delete borna[key])
  return Object.assign(baseFields(image), CARRIER_TEXT, {
    type: 'text',
    text: source,
    originalText: source,
    customData: { borna },
  })
}

function svgDataUrl(svg) {
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`
}

/**
 * Draws one equation with the engine that is already loaded and remembers
 * that this client made it: the one way a runtime equation file comes to be.
 * The file is local and content-addressed; it is never persisted and never
 * taken from anywhere else (a clipboard, a server, another client).
 * @param {string} source -- LaTeX
 * @returns {{fileId: string, metrics: {widthEm: number, heightEm: number},
 *   file: {id: string, mimeType: string, dataURL: string, created: number}}}
 * @throws {BoardEquationError} a refused or invalid source, or
 *   `engine_unavailable` when the engine is not loaded
 */
export function equationPictureFor(source) {
  const picture = renderEquationNow(source)
  const fileId = picture.contentId
  const made = runtimeEquationFiles.get(fileId)
  if (made) made.sources.add(source)
  else {
    runtimeEquationFiles.set(fileId, {
      metrics: picture.metrics,
      sources: new Set([source]),
    })
  }
  return {
    fileId,
    metrics: picture.metrics,
    file: {
      id: fileId,
      mimeType: 'image/svg+xml',
      dataURL: svgDataUrl(picture.svg),
      created: Date.now(),
    },
  }
}

export function isSupportedBoardElement(element) {
  return (
    isPlainObject(element) &&
    !element.isDeleted &&
    (BOARD_ELEMENT_TYPES.indexOf(element.type) !== -1 ||
      isRuntimeEquationImage(element))
  )
}

function encodeElement(element) {
  if (element.type === 'image')
    return encodeElement(equationImageToCarrier(element))
  const out = {}
  BASE_KEYS.concat(TYPE_KEYS[element.type]).forEach(key => {
    if (element[key] !== undefined) out[key] = element[key]
  })
  out.isDeleted = false
  out.link = null
  out.frameId = null
  // WB-M1B S5 (doc 23 §27): a pen stroke from a device that reports a
  // pressure outside 0..1 is written without pressure (`boardStroke.js`) --
  // also while it is still being drawn, so a save is never refused for it.
  if (hasUnusablePressure(element)) Object.assign(out, strokeWithoutPressure())
  if (
    isPlainObject(element.customData) &&
    isPlainObject(element.customData.borna)
  ) {
    out.customData = { borna: element.customData.borna }
  }
  return out
}

function encodeView(appState) {
  const state = appState || {}
  const rawZoom = isPlainObject(state.zoom) ? state.zoom.value : state.zoom
  const zoom = finite(rawZoom, 1)
  return {
    scrollX: finite(state.scrollX, 0),
    scrollY: finite(state.scrollY, 0),
    zoom: Math.min(BOARD_MAX_ZOOM, Math.max(BOARD_MIN_ZOOM, zoom)),
  }
}

/**
 * The editor's scene -> the persisted envelope.
 * @param {{elements?: ReadonlyArray<object>, appState?: object}} scene
 */
export function encodeBoardScene(scene) {
  const elements = ((scene && scene.elements) || [])
    .filter(isSupportedBoardElement)
    .map(encodeElement)
  // A plain-JSON copy: whatever the student draws next cannot change the
  // scene that is being sent.
  return JSON.parse(
    JSON.stringify({
      schemaVersion: BOARD_SCENE_SCHEMA_VERSION,
      format: BOARD_SCENE_FORMAT,
      elements,
      files: {},
      view: encodeView(scene && scene.appState),
    })
  )
}

/**
 * The persisted envelope -> what the editor mounts. Throws for an envelope
 * this build cannot read (unknown schema version or format): a board is
 * never shown as empty because it could not be understood.
 * @param {object} envelope
 * @returns {{elements: object[], files: object, view: {scrollX: number, scrollY: number, zoom: number}}}
 */
export function decodeBoardScene(envelope) {
  if (
    !isPlainObject(envelope) ||
    envelope.schemaVersion !== BOARD_SCENE_SCHEMA_VERSION ||
    envelope.format !== BOARD_SCENE_FORMAT ||
    !Array.isArray(envelope.elements)
  ) {
    const error = new Error('Unsupported board scene')
    error.code = 'BOARD_SCENE_UNSUPPORTED'
    throw error
  }
  return {
    elements: envelope.elements.filter(isSupportedBoardElement),
    files: {},
    view: encodeView(envelope.view),
  }
}

/**
 * The WB-M1B hook (reserved in M1A): turns the decoded scene's valid
 * equation carriers into runtime images and returns the files they show,
 * before the editor mounts.
 *
 * The frozen `aiTutorBoard` store calls this with the decoded scene and then
 * mounts `scene.elements` itself, so this is the one place the codec changes
 * an array it was given: each valid carrier's slot in `scene.elements` is
 * REPLACED by a new image object (no element object is mutated; ids,
 * versions and versionNonces are kept, so the scene version -- the dirty
 * baseline -- is unchanged).
 *
 * - No valid carrier: returns {} and the renderer is never loaded.
 * - The renderer is loaded once; each distinct source is drawn once.
 * - Anything that fails (engine unavailable, a source MathJax refuses, a
 *   picture the policy refuses) leaves that carrier as the text element it
 *   is. Nothing is logged with the source in it.
 * @param {{elements: object[]}} scene
 * @returns {Promise<object>} the editor's `files` map (Excalidraw
 *   BinaryFiles), keyed by the pictures' content ids
 */
export async function rehydrateBoardFiles(scene) {
  const elements = scene && Array.isArray(scene.elements) ? scene.elements : []
  const slots = []
  elements.forEach((element, index) => {
    if (equationCarrierStatus(element) === 'valid') slots.push(index)
  })
  if (slots.length === 0) return {}
  try {
    await loadEquationEngine()
  } catch (err) {
    return {}
  }
  const pictures = new Map()
  const files = {}
  slots.forEach(index => {
    const carrier = elements[index]
    const source = carrier.text
    if (!pictures.has(source)) {
      let picture = null
      try {
        picture = equationPictureFor(source)
      } catch (err) {
        picture = null
      }
      pictures.set(source, picture)
    }
    const picture = pictures.get(source)
    if (!picture) return
    const fileId = picture.fileId
    if (!files[fileId]) files[fileId] = picture.file
    elements[index] = equationCarrierToImage(carrier, picture.metrics, fileId)
  })
  return files
}

// Spec-only: forget the files this client made.
export function resetBoardEquationRuntimeForTests() {
  runtimeEquationFiles.clear()
}

/**
 * Excalidraw's own scene version: the sum of the element versions. Every
 * edit (a deletion included) raises one element's version.
 * @param {ReadonlyArray<object>} elements
 */
export function sceneVersionOf(elements) {
  return (elements || []).reduce(
    (total, element) => total + ((element && element.version) || 0),
    0
  )
}

/** UTF-8 size of an envelope as it would be sent. */
export function boardSceneBytes(envelope) {
  const text = JSON.stringify(envelope)
  if (typeof TextEncoder === 'function') {
    return new TextEncoder().encode(text).length
  }
  return unescape(encodeURIComponent(text)).length
}

export function emptyBoardScene() {
  return encodeBoardScene({ elements: [], appState: {} })
}
